/**
 * «О бизнесе» (Spec 018): the first page of context — who we are, what we do,
 * the projects and the shared rules. Agents read the same page first.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Business, Projects } from "@/api/resources";
import { PassportCard, freshnessLabel } from "@/components/Passport";
import { Button } from "@/components/ui/Button";
import { MyAccesses, ResourcesSection } from "@/components/Resources";
import { MySecrets } from "@/components/Secrets";
import { Card, ClickableCard } from "@/components/ui/Card";
import { Section } from "@/components/ui/Section";
import { Spinner } from "@/components/ui/Spinner";
import { useToast } from "@/components/ui/Toast";

export function BusinessView() {
  const qc = useQueryClient();
  const toast = useToast();
  const business = useQuery({ queryKey: ["business"], queryFn: Business.get });
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list() });

  if (business.isLoading) return <Spinner />;
  if (business.isError || !business.data) {
    return (
      <Card role="alert" className="space-y-2">
        <p>Не удалось загрузить паспорт бизнеса.</p>
        <Button size="compact" variant="ghost" onClick={() => void business.refetch()}>
          Повторить
        </Button>
      </Card>
    );
  }
  const b = business.data;
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["business"] }),
      qc.invalidateQueries({ queryKey: ["business-revisions"] }),
    ]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">О бизнесе</h1>
      <PassportCard
        title="Паспорт бизнеса"
        body={b.body_md}
        version={b.version}
        bodyKey="body_md"
        template={b.template_md}
        freshness={b.freshness}
        checkedAt={b.checked_at}
        checkedBy={b.checked_by_name}
        historyKey={["business-revisions"]}
        loadHistory={Business.revisions}
        onSave={async (body, source, version) => {
          await Business.update({ body_md: body, version, source_md: source });
          await refresh();
          toast.success("Паспорт бизнеса сохранён");
        }}
        onCheck={async () => {
          await Business.check(b.version);
          await refresh();
          toast.success("Отмечено: паспорт актуален");
        }}
        onRefresh={() => void refresh()}
      />
      <Section title="Проекты">
        {(projects.data ?? [])
          .filter((p) => !p.archived_at)
          .map((p) => (
            <ClickableCard key={p.id} to={`/projects/${p.id}`} ariaLabel={`Проект ${p.title}`}>
              <p className="text-cell-title font-medium text-text">{p.title}</p>
              {p.summary && <p className="text-body text-hint">{p.summary}</p>}
              <p className="text-meta text-hint">
                Паспорт:{" "}
                {freshnessLabel(p.passport_freshness, p.passport_checked_at, p.passport_checked_by_name)}
              </p>
            </ClickableCard>
          ))}
      </Section>
      <ResourcesSection title="Ресурсы бизнеса" />
      <MyAccesses />
      <MySecrets />
    </div>
  );
}
